import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import Footer from '../components/shared/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RatingBadge } from '../components/shared/Badges';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Truck, Plane, Ship, Train, Package, MapPin, Calendar,
  ArrowRight, Clock, DollarSign, Star, Loader2, Search
} from 'lucide-react';

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco',
  'Tanzania', 'Ethiopia', 'Rwanda', 'Senegal', 'Cameroon', 'Uganda'
];

const cargoTypes = [
  { value: 'general', label: 'General Cargo' },
  { value: 'container', label: 'Container (FCL)' },
  { value: 'bulk', label: 'Bulk Cargo' },
  { value: 'perishable', label: 'Perishable Goods' },
  { value: 'hazardous', label: 'Hazardous Materials' },
];

const LogisticsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');

  const [formData, setFormData] = useState({
    origin_country: '',
    origin_city: '',
    destination_country: '',
    destination_city: '',
    cargo_type: 'general',
    weight_kg: '',
    pickup_date: '',
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getQuotes = async () => {
    if (!formData.origin_country || !formData.destination_country || !formData.weight_kg) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/logistics/quotes`, {
        origin_country: formData.origin_country,
        origin_city: formData.origin_city || formData.origin_country,
        destination_country: formData.destination_country,
        destination_city: formData.destination_city || formData.destination_country,
        cargo_type: formData.cargo_type,
        weight_kg: parseFloat(formData.weight_kg),
      });
      
      setQuotes(response.data);
      setStep(2);
    } catch (error) {
      toast.error('Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  };

  const bookShipment = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to book a shipment');
      navigate('/login');
      return;
    }

    if (!selectedQuote) {
      toast.error('Please select a provider');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.post(
        `${API}/logistics/book`,
        {
          provider_id: selectedQuote.provider_id,
          origin_country: formData.origin_country,
          origin_city: formData.origin_city || formData.origin_country,
          origin_address: 'To be provided',
          destination_country: formData.destination_country,
          destination_city: formData.destination_city || formData.destination_country,
          destination_address: 'To be provided',
          cargo_type: formData.cargo_type,
          weight_kg: parseFloat(formData.weight_kg),
          pickup_date: formData.pickup_date,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          // withCredentials removed for CORS compatibility
        }
      );
      
      setTrackingNumber(response.data.tracking_number);
      setBookingComplete(true);
      toast.success('Booking confirmed!');
    } catch (error) {
      toast.error('Failed to book shipment');
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="w-5 h-5" />;
      case 'sea': return <Ship className="w-5 h-5" />;
      case 'rail': return <Train className="w-5 h-5" />;
      default: return <Truck className="w-5 h-5" />;
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container-main py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Truck className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-charcoal mb-4 font-poppins">
              Booking Confirmed!
            </h1>
            <p className="text-gray-600 mb-4">
              Your shipment has been booked with {selectedQuote?.name}.
            </p>
            <Card className="mb-6">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500 mb-1">Tracking Number</p>
                <p className="text-2xl font-bold font-mono text-continental">{trackingNumber}</p>
              </CardContent>
            </Card>
            <div className="flex justify-center gap-4">
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                Go to Dashboard
              </Button>
              <Button onClick={() => { setBookingComplete(false); setStep(1); setQuotes([]); }} variant="outline">
                New Booking
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className={isAuthenticated ? 'flex' : ''}>
        {/* Sidebar for authenticated users */}
        {isAuthenticated && <DashboardSidebar />}

        <div className={isAuthenticated ? 'flex-1' : ''}>
          {/* Hero */}
          <section className={`bg-charcoal text-white py-12 ${isAuthenticated ? 'mx-6 mt-6 rounded-lg' : ''}`} data-testid="logistics-hero">
            <div className="container-main">
              <Badge className="bg-logistics text-white mb-4">Logistics Network</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
                Ship Goods Across Africa
              </h1>
              <p className="text-gray-300 max-w-xl">
                Compare quotes from trusted logistics providers. Book freight forwarding, 
                shipping, trucking, and air cargo with tracking.
              </p>
            </div>
          </section>

          <div className={isAuthenticated ? 'p-6' : 'container-main py-12'}>
            {/* Step 1: Quote Request */}
            {step === 1 && (
              <Card className="max-w-2xl mx-auto" data-testid="logistics-step-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-continental" />
                    Get Shipping Quotes
                  </CardTitle>
                  <CardDescription>Enter your shipment details to compare providers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Origin Country *</Label>
                      <Select value={formData.origin_country} onValueChange={(v) => handleChange('origin_country', v)}>
                        <SelectTrigger className="mt-1" data-testid="origin-country-select">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                </div>
                <div>
                  <Label>Origin City</Label>
                  <Input
                    value={formData.origin_city}
                    onChange={(e) => handleChange('origin_city', e.target.value)}
                    placeholder="e.g., Lagos"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Destination Country *</Label>
                  <Select value={formData.destination_country} onValueChange={(v) => handleChange('destination_country', v)}>
                    <SelectTrigger className="mt-1" data-testid="destination-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.filter(c => c !== formData.origin_country).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Destination City</Label>
                  <Input
                    value={formData.destination_city}
                    onChange={(e) => handleChange('destination_city', e.target.value)}
                    placeholder="e.g., Nairobi"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Cargo Type</Label>
                  <Select value={formData.cargo_type} onValueChange={(v) => handleChange('cargo_type', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cargoTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Weight (kg) *</Label>
                  <Input
                    type="number"
                    value={formData.weight_kg}
                    onChange={(e) => handleChange('weight_kg', e.target.value)}
                    placeholder="e.g., 500"
                    className="mt-1"
                    data-testid="weight-input"
                  />
                </div>
                <div>
                  <Label>Pickup Date</Label>
                  <Input
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => handleChange('pickup_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <Button onClick={getQuotes} className="w-full btn-primary" disabled={loading} data-testid="get-quotes-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Getting Quotes...
                  </>
                ) : (
                  <>
                    Get Quotes
                    <Search className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Provider */}
        {step === 2 && (
          <div className="max-w-3xl mx-auto" data-testid="logistics-step-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-charcoal">Available Providers</h2>
                <p className="text-gray-500">
                  {formData.origin_country} → {formData.destination_country} · {formData.weight_kg}kg
                </p>
              </div>
              <Button variant="outline" onClick={() => setStep(1)}>
                Modify Search
              </Button>
            </div>

            <div className="space-y-4 mb-6">
              {quotes.map((quote) => (
                <Card 
                  key={quote.provider_id}
                  className={`cursor-pointer transition-all ${
                    selectedQuote?.provider_id === quote.provider_id 
                      ? 'border-continental ring-2 ring-continental/20' 
                      : 'hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedQuote(quote)}
                  data-testid={`quote-${quote.provider_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                          {getModeIcon(quote.transport_mode)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-charcoal">{quote.name}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <Badge variant="outline" className="capitalize">{quote.transport_mode}</Badge>
                            <RatingBadge rating={quote.rating} />
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-continental">
                          {quote.currency} {quote.price.toLocaleString()}
                        </p>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {quote.estimated_days} days
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={bookShipment} 
                className="btn-primary" 
                disabled={!selectedQuote || loading}
                data-testid="book-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    Book Shipment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default LogisticsPage;
